import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
  Chip
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { tenantApiFetch } from './tenantApi';

function buildParams(state) {
  const params = new URLSearchParams({
    page: String(state.page + 1),
    pageSize: String(state.pageSize),
    sort: state.sort,
    order: state.order
  });
  if (state.appliedQ) params.set('q', state.appliedQ);
  if (state.appliedStatus) params.set('status', state.appliedStatus);
  if (state.appliedAuthMethod) params.set('authMethod', state.appliedAuthMethod);
  if (state.appliedUserId) params.set('userId', state.appliedUserId);
  if (state.appliedIp) params.set('ip', state.appliedIp);
  if (state.appliedDateFrom) params.set('dateFrom', state.appliedDateFrom);
  if (state.appliedDateTo) params.set('dateTo', state.appliedDateTo);
  return params;
}

function statusColor(s) {
  if (s === 'active') return 'success';
  if (s === 'failed') return 'error';
  if (s === 'ended' || s === 'expired') return 'default';
  return 'default';
}

export default function TenantSessionsListPage() {
  const [qInput, setQInput] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [statusInput, setStatusInput] = useState('');
  const [appliedStatus, setAppliedStatus] = useState('');
  const [authInput, setAuthInput] = useState('');
  const [appliedAuthMethod, setAppliedAuthMethod] = useState('');
  const [userIdInput, setUserIdInput] = useState('');
  const [appliedUserId, setAppliedUserId] = useState('');
  const [ipInput, setIpInput] = useState('');
  const [appliedIp, setAppliedIp] = useState('');
  const [dateFromInput, setDateFromInput] = useState('');
  const [appliedDateFrom, setAppliedDateFrom] = useState('');
  const [dateToInput, setDateToInput] = useState('');
  const [appliedDateTo, setAppliedDateTo] = useState('');
  const [sort, setSort] = useState('login_at');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = buildParams({
      page,
      pageSize,
      sort,
      order,
      appliedQ,
      appliedStatus,
      appliedAuthMethod,
      appliedUserId,
      appliedIp,
      appliedDateFrom,
      appliedDateTo
    });
    try {
      const res = await tenantApiFetch(`/api/tenant/login-sessions?${params}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load sessions');
      setData(json);
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    pageSize,
    sort,
    order,
    appliedQ,
    appliedStatus,
    appliedAuthMethod,
    appliedUserId,
    appliedIp,
    appliedDateFrom,
    appliedDateTo
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const applyFilters = () => {
    setPage(0);
    setAppliedQ(qInput.trim());
    setAppliedStatus(statusInput);
    setAppliedAuthMethod(authInput);
    setAppliedUserId(userIdInput.trim());
    setAppliedIp(ipInput.trim());
    setAppliedDateFrom(dateFromInput.trim());
    setAppliedDateTo(dateToInput.trim());
  };

  const entries = data?.entries || [];
  const total = data?.pagination?.total ?? 0;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Login &amp; session activity
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Successful logins, failures (after migration), and session lifecycle for this tenant. IP addresses are masked.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
          <TextField
            size="small"
            label="Search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Email, id, failure reason…"
            sx={{ minWidth: 220 }}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
          />
          <TextField
            size="small"
            label="User ID"
            value={userIdInput}
            onChange={(e) => setUserIdInput(e.target.value)}
            sx={{ minWidth: 100 }}
          />
          <TextField
            size="small"
            label="IP contains"
            value={ipInput}
            onChange={(e) => setIpInput(e.target.value)}
            sx={{ minWidth: 140 }}
          />
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={statusInput} onChange={(e) => setStatusInput(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="ended">Ended</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Auth method</InputLabel>
            <Select label="Auth method" value={authInput} onChange={(e) => setAuthInput(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="password">Password</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="From date"
            type="date"
            value={dateFromInput}
            onChange={(e) => setDateFromInput(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            label="To date"
            type="date"
            value={dateToInput}
            onChange={(e) => setDateToInput(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Sort</InputLabel>
            <Select label="Sort" value={sort} onChange={(e) => setSort(e.target.value)}>
              <MenuItem value="login_at">Login time</MenuItem>
              <MenuItem value="last_activity_at">Last activity</MenuItem>
              <MenuItem value="id">ID</MenuItem>
              <MenuItem value="status">Status</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Order</InputLabel>
            <Select label="Order" value={order} onChange={(e) => setOrder(e.target.value)}>
              <MenuItem value="desc">Newest</MenuItem>
              <MenuItem value="asc">Oldest</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" onClick={applyFilters}>
            Apply filters
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Login</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Auth</TableCell>
                  <TableCell>IP</TableCell>
                  <TableCell>Last activity</TableCell>
                  <TableCell align="right">Detail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.login_at ? String(row.login_at) : '—'}</TableCell>
                    <TableCell>{row.user_email || (row.user_id != null ? `#${row.user_id}` : '—')}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.status} color={statusColor(row.status)} variant="outlined" />
                    </TableCell>
                    <TableCell>{row.auth_method || '—'}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {row.ip_address || '—'}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {row.last_activity_at ? String(row.last_activity_at) : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton component={Link} to={`/tenant/sessions/${row.id}`} size="small" aria-label="Detail">
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {entries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>No sessions match these filters.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_e, p) => setPage(p)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}
